// Cruce del padrón de `retail-org` con las KPIs del periodo — lógica pura, sin React.
// El padrón pone las filas; las KPIs, los números. Quien no vendió queda en ceros.
import type { RetailUser, Store, Zone } from '@/api/services/retail-org/retail-org.client';
import type { SellerKpiRow, StoreKpiRow } from '@/api/services/retail-org/backoffice';
import { RetailUserStatusEnum } from '@fiado/type-kit/bin/retailOrg/enums/RetailUserStatusEnum';
import { StoreStatusEnum } from '@fiado/type-kit/bin/retailOrg/enums/StoreStatusEnum';

/** El tier de los cobradores; `retail-org` considera vendedor a todo el que no lo tiene. */
export const COLLECTOR_TIER = 'collector';

/** Mismo criterio que `listSellersByRetailer` de `retail-org`: vendedor es quien no es cobrador. */
export function isSeller(user: RetailUser): boolean {
    return user.commissionTier !== COLLECTOR_TIER;
}

export function zoneNameById(zones: readonly Zone[]): Map<string, string> {
    return new Map(zones.map((z) => [z.zoneId, z.name]));
}

/** Tiendas del padrón (activas, o con venta en el periodo) con sus KPIs; sin venta, en ceros. */
export function mergeStoreRoster(
    stores: readonly Store[],
    kpis: readonly StoreKpiRow[],
    zoneNames: ReadonlyMap<string, string>,
): StoreKpiRow[] {
    const kpiById = new Map(kpis.map((row) => [row.storeId, row]));
    const rows: StoreKpiRow[] = [];
    for (const store of stores) {
        const kpi = kpiById.get(store.storeId);
        kpiById.delete(store.storeId);
        if (!kpi && store.status !== StoreStatusEnum.ACTIVE) continue;
        const zone = store.zoneId === null ? null : (zoneNames.get(store.zoneId) ?? null);
        rows.push({
            ...(kpi ?? zeroStoreRow(store.storeId)),
            name: store.name,
            code: store.code,
            retailerId: store.retailerId,
            zone: kpi?.zone ?? zone,
        });
    }
    // Una venta de una tienda que el padrón ya no lista se conserva: su dinero no se esconde.
    return [...rows, ...kpiById.values()];
}

function zeroStoreRow(storeId: string): StoreKpiRow {
    return {
        storeId,
        retailerId: null,
        code: null,
        name: storeId,
        zone: null,
        kind: null,
        activeSellerCount: null,
        unitsToday: 0,
        soldTodayCents: 0,
        weekOverWeekChange: null,
        unitsLastWeek: null,
        soldLastWeekCents: null,
        averageTicketCents: null,
        dailyGoalCents: null,
    };
}

/** Vendedores del padrón (activos, o con venta en el periodo) con sus KPIs; sin venta, en ceros. */
export function mergeSellerRoster(
    users: readonly RetailUser[],
    kpis: readonly SellerKpiRow[],
    stores: readonly Store[],
    zoneNames: ReadonlyMap<string, string>,
): SellerKpiRow[] {
    const kpiById = new Map(kpis.map((row) => [row.sellerId, row]));
    const storeById = new Map(stores.map((s) => [s.storeId, s]));
    const rows: SellerKpiRow[] = [];
    const zoneOf = (storeId: string | null) => {
        const zoneId = storeId === null ? null : (storeById.get(storeId)?.zoneId ?? null);
        return zoneId === null ? null : (zoneNames.get(zoneId) ?? null);
    };
    for (const user of users) {
        const kpi = kpiById.get(user.retailUserId);
        kpiById.delete(user.retailUserId);
        // Quien vendió se queda aunque el padrón lo tenga como cobrador o inactivo.
        if (!kpi && (!isSeller(user) || user.status !== RetailUserStatusEnum.ACTIVE)) continue;
        const storeId = kpi?.storeId ?? user.homeStoreId;
        const store = storeById.get(storeId);
        rows.push({
            ...(kpi ?? zeroSellerRow(user.retailUserId)),
            name: user.displayName ?? kpi?.name ?? user.retailUserId,
            storeId,
            retailerId: user.retailerId,
            storeName: kpi?.storeName ?? store?.name ?? null,
            zone: zoneOf(storeId),
            isStoreManager: store ? store.managerRetailUserId === user.retailUserId : null,
        });
    }
    const orphans = [...kpiById.values()].map((row) => ({ ...row, zone: zoneOf(row.storeId) }));
    return [...rows, ...orphans];
}

function zeroSellerRow(sellerId: string): SellerKpiRow {
    return {
        sellerId,
        name: sellerId,
        storeId: null,
        retailerId: null,
        storeName: null,
        zone: null,
        isStoreManager: null,
        unitsToday: 0,
        soldTodayCents: 0,
        averageTicketCents: null,
        dailyGoalCents: null,
    };
}

/** Vendió en el periodo: al menos una pieza o un peso. */
export function hasSales(row: { unitsToday: number | null; soldTodayCents: number | null }) {
    return (row.unitsToday ?? 0) > 0 || (row.soldTodayCents ?? 0) > 0;
}
